import { ARCHITECTURE_VERSION, FORMAT, GEAR, GEAR_CAPTURE_MAP } from '@/utils/constants';
import { creatorName, toTonePathname } from '@/utils/helpers';
import { GearIcon, ICON_BG_COLOR_MAP, toCatalogGear } from '@/components/icons/Gear';
import {
  DEFAULT_CAB_IRS,
  DEFAULT_INPUTS,
  DEFAULT_MODELS,
  DEFAULT_REVERB_IRS,
  NO_IR,
  NO_MODEL,
  type IR,
  type Input,
  type Model,
  type NonEmptyArray,
  type PlayerAssets,
  type PlayerTone,
  type ToneMeta,
} from 'neural-amp-modeler-wasm';
import { modelFileUrl } from '@/utils/models/file-url';
import { companionFor, irMixFor, type Companion } from './gear-matrix';

/**
 * The one mapper from a tone3000 tone row to the player's `PlayerTone`.
 * Every surface (tone page, cards, carousels, Select preview, embeds) goes
 * through here so they all pick the same model, the same IR and the same DI
 * for a given tone and user config.
 */

/** What a player needs from a `models` row. Partial so RPC shapes fit. */
export interface PlayableModelRow {
  id?: number | null;
  name?: string | null;
  model_url?: string | null;
  architecture_version?: string | null;
}

/** What a player needs from a tone row (tones_counts / RPC / card). */
export interface PlayableToneRow {
  id?: number | null;
  title?: string | null;
  images?: string[] | null;
  platform?: string | null;
  gear?: string | null;
  tags?: Array<{ name?: string | null } | null> | null;
  user?: {
    username?: string | null;
    display_name?: string | null;
    avatar_url?: string | null;
    is_verified?: boolean | null;
  } | null;
}

/** Per-instrument choices the user made last time (PlayerConfigContext). */
export interface PlayerDefaults {
  inputs: { guitar: Input; bass: Input };
  ampIrs: { guitar: IR; bass: IR };
  /** Cab behind pedal captures, kept apart from the amp cab. */
  pedalIrs: { guitar: IR; bass: IR };
  models: { guitar: Model; bass: Model };
}

export interface ToPlayerToneOptions {
  config: PlayerDefaults;
  /** Link targets. Select-flow pages route through the OAuth-aware prefix. */
  hrefFor?: (tone: PlayableToneRow) => string | undefined;
  userHrefFor?: (tone: PlayableToneRow) => string | undefined;
}

const getExtension = (url: string) => url.split('.').pop()?.toLowerCase();

/** `toTonePathname` for the loose row shape the player works with. */
export const tonePath = (tone: Pick<PlayableToneRow, 'id' | 'title'>) =>
  toTonePathname({ id: tone.id ?? undefined, title: tone.title ?? undefined });

type FileRow = PlayableModelRow & { id: number; model_url: string };

/** Through the site's file route: stored refs (r2://…) aren't browser URLs, and private tones need the check. */
const hasFile = (m: PlayableModelRow): m is FileRow => !!m.model_url && typeof m.id === 'number';

/** Playable NAM models: a `.nam` file and A2 — the player loads nothing else. `token` opens a shared private tone. */
export const getUsableModels = (models: PlayableModelRow[], token?: string | null): Model[] =>
  models
    .filter(
      (m): m is FileRow =>
        hasFile(m) &&
        getExtension(m.model_url) === 'nam' &&
        m.architecture_version === ARCHITECTURE_VERSION.A2
    )
    .map((m) => ({ name: m.name ?? 'Model', url: modelFileUrl(m, { token }) }));

/** Playable IRs: a `.wav` file. */
export const getUsableIrs = (models: PlayableModelRow[], token?: string | null): IR[] =>
  models
    .filter((m): m is FileRow => hasFile(m) && getExtension(m.model_url) === 'wav')
    .map((m) => ({ name: m.name ?? 'IR', url: modelFileUrl(m, { token }) }));

export const isBassTone = (tags: PlayableToneRow['tags']) =>
  !!tags?.some((tag) => tag?.name === 'bass');

const instrument = (isBass: boolean): 'bass' | 'guitar' => (isBass ? 'bass' : 'guitar');

/**
 * Mark `chosen` as the default. When it isn't in the list (the user's last
 * pick was from another pool), the list's own default stands.
 */
const withDefault = <T extends { name: string; default?: boolean }>(
  items: T[],
  chosen: T | undefined
): T[] => {
  const found = !!chosen && items.some((item) => item.name === chosen.name);
  return found ? items.map((item) => ({ ...item, default: chosen!.name === item.name })) : items;
};

const nonEmpty = <T,>(items: T[]): NonEmptyArray<T> | null =>
  items.length > 0 ? (items as NonEmptyArray<T>) : null;

/** The companion's option list with its default applied. */
const companionOptions = (
  companion: Companion,
  inst: 'guitar' | 'bass',
  config: PlayerDefaults
): { irs?: NonEmptyArray<IR>; models?: NonEmptyArray<Model> } => {
  const user = companion.default === 'user';
  switch (companion.pool) {
    case 'cab': {
      const chosen =
        companion.default === 'pedal' ? config.pedalIrs[inst] : user ? config.ampIrs[inst] : NO_IR;
      return { irs: withDefault(DEFAULT_CAB_IRS, chosen) as NonEmptyArray<IR> };
    }
    case 'reverb':
      return {
        irs: withDefault(DEFAULT_REVERB_IRS, user ? config.ampIrs[inst] : NO_IR) as NonEmptyArray<IR>,
      };
    case 'amp':
      return {
        models: withDefault(
          [NO_MODEL, ...DEFAULT_MODELS],
          user ? config.models[inst] : NO_MODEL
        ) as NonEmptyArray<Model>,
      };
  }
};

/**
 * Build the player's assets for a tone's models. `null` when the tone has
 * nothing the player can play (no A2 for a NAM tone, no wav for an IR tone).
 *
 * The companion select, its options and default come from `GEAR_MATRIX`
 * (see `gear-matrix.ts` for the table). In the engine's `pre → model → IR`
 * chain the tone takes its own slot and the companion fills the slot the
 * gear calls for: an IR pool after a NAM tone, an amp ahead of an IR tone,
 * or — for cabinet captures — an amp ahead of the NAM via `preModels`.
 */
export const toPlayerAssets = (
  models: PlayableModelRow[],
  tone: Pick<PlayableToneRow, 'platform' | 'gear' | 'tags'>,
  config: PlayerDefaults,
  token?: string | null
): PlayerAssets | null => {
  const platform = tone.platform as FORMAT;
  const gear = (tone.gear as GEAR | null) ?? undefined;
  const inst = instrument(isBassTone(tone.tags));

  const inputs = nonEmpty(withDefault(DEFAULT_INPUTS, config.inputs[inst]));
  if (!inputs) return null;

  if (platform === FORMAT.NAM) {
    const toneModels = nonEmpty(getUsableModels(models, token));
    if (!toneModels) return null;
    const companion = companionFor(gear, FORMAT.NAM);
    const options = companionOptions(companion, inst, config);
    if (options.models) {
      // Cabinet capture: the companion amp drives the tone's model.
      return {
        models: toneModels,
        preModels: options.models,
        irs: [NO_IR],
        inputs,
        previewMode: 'model',
        labels: { preModel: companion.label },
      };
    }
    return {
      models: toneModels,
      irs: options.irs!,
      inputs,
      previewMode: 'model',
      labels: { ir: companion.label },
    };
  }

  if (platform === FORMAT.IR) {
    const mix = irMixFor(gear);
    const toneIrs = nonEmpty(
      getUsableIrs(models, token).map((ir) => (mix === undefined ? ir : { ...ir, mix }))
    );
    if (!toneIrs) return null;
    const companion = companionFor(gear, FORMAT.IR);
    const options = companionOptions(companion, inst, config);
    return {
      models: options.models!,
      irs: toneIrs,
      inputs,
      previewMode: 'ir',
      labels: { model: companion.label },
    };
  }

  return null;
};

/** Whether the player can preview this format at all. */
export const isPlayablePlatform = (platform?: string | null) =>
  platform === FORMAT.NAM || platform === FORMAT.IR;

export const toPlayerMeta = (
  tone: PlayableToneRow,
  options: Pick<ToPlayerToneOptions, 'hrefFor' | 'userHrefFor'> = {}
): ToneMeta => {
  const gear = toCatalogGear(tone.gear as GEAR | undefined);
  const user = tone.user;
  return {
    title: tone.title ?? '',
    imageUrl: tone.images?.[0] ?? undefined,
    // Same stand-in as ToneCard: the gear icon on its tinted square.
    imageFallback: (
      <div
        className="w-full h-full flex items-center justify-center"
        style={{ backgroundColor: ICON_BG_COLOR_MAP[gear] }}
      >
        <GearIcon size={24} gear={gear} isColor />
      </div>
    ),
    href: options.hrefFor ? options.hrefFor(tone) : `/tones/${tonePath(tone)}`,
    subtitle: GEAR_CAPTURE_MAP[gear],
    format: tone.platform ?? undefined,
    author: user
      ? {
          name: creatorName({
            username: user.username ?? undefined,
            display_name: user.display_name,
            is_verified: !!user.is_verified,
          }),
          avatarUrl: user.avatar_url ?? undefined,
          href: options.userHrefFor
            ? options.userHrefFor(tone)
            : user.username
              ? `/${user.username}`
              : undefined,
          verified: !!user.is_verified,
        }
      : undefined,
  };
};

/**
 * A tone row plus its (eager or lazy) assets → `PlayerTone`. Pass `assets`
 * as a thunk for cards so the model list is fetched on first play.
 */
export const toPlayerTone = (
  tone: PlayableToneRow,
  assets: PlayerAssets | (() => Promise<PlayerAssets>),
  options: Pick<ToPlayerToneOptions, 'hrefFor' | 'userHrefFor'> & { meta?: boolean } = {}
): PlayerTone => ({
  id: String(tone.id),
  meta: options.meta === false ? undefined : toPlayerMeta(tone, options),
  assets,
});
